import type { Charge, FitMode, FitSpec, Flap, Flow, HideGroup, ModelLayers, ModelSurface, Orbit } from "./bigTypes";
export type { ModelSurface } from "./bigTypes";

/**
 * The skin kit's data model (skins overhaul, 2026-10-01: "none should just be a colour change, they should have new
 * animations, features and so on"). A tower skin is no longer a tint: it is
 *  - a material SET — its own albedo grade, roughness, metalness, emissive and a procedural surface pattern for each
 *    material slot of the stock tower models (stone, wood, roof, metal, cloth; the element parts are never touched),
 *  - ATTACHMENTS — small props from the skin's own GLB (`art/hq/skins/<skin>/kit.glb`) mounted on the tower's sockets
 *    (art/hq/anchors.json `sockets`, tools/art/skins/sockets.mjs), riding the part they stand on through every clip,
 *  - AMBIENT MOTION for those props (spin, bob, sway, pulse, cloth wave, twinkle, glow pulse) on the GPU,
 *  - an IDLE VFX and an ATTACK FLOURISH through the level's VFX router.
 * The fortress and troop skins are whole replacement models; a road skin is a tint plus props scattered along the
 * built road. The contract for authors is tools/art/skins/README.md.
 *
 * Pure data: nothing here imports three.js, so the registry and its budget checks are unit-testable.
 */

/** The material slots of a stock tower model: every part node maps to one (kit/slots.ts SLOT_OF_PART). */
export type SlotName = "stone" | "wood" | "roof" | "metal" | "cloth";
export const SLOT_NAMES: readonly SlotName[] = ["stone", "wood", "roof", "metal", "cloth"];

/** A procedural surface pattern drawn over a slot's albedo (kit/bodyMaterial.ts). */
export type PatternKind = "none" | "veins" | "grain" | "hammered" | "flecks" | "plates";
export const PATTERN_KINDS: readonly PatternKind[] = ["none", "veins", "grain", "hammered", "flecks", "plates"];

/** One slot's new material (colours sRGB hex). Every field is optional: what is left out stays as painted. */
export interface SlotLook {
  /** The hue the albedo is swapped toward, keeping the texture's own light and dark (as BodyLook.tint). */
  readonly tint?: number;
  /** 0 = the stock albedo, 1 = fully the tint's hue. */
  readonly mix?: number;
  /** Brightness multiplier (1 = as painted). */
  readonly gain?: number;
  readonly metalness?: number;
  readonly roughness?: number;
  /** Leave strongly coloured painted texels as they are (default true). */
  readonly keepAccents?: boolean;
  /** Emissive on the palest texels (bands, rims, carved trims): colour and HDR strength (> 1 blooms). */
  readonly trim?: { readonly colour: number; readonly strength: number };
  /**
   * A surface pattern: `scale` cycles per metre, `strength` 0..1 of its albedo effect, `colour` what it paints, and
   * `glow` an HDR emissive along it (obsidian's lit seams, voidglass's veins; `plates`: the seams between the panels,
   * forgeguard's hot seams; 0 = none). `flecks` and `veins` glow
   * pulse slowly when motion is on.
   */
  readonly pattern?: { readonly kind: PatternKind; readonly colour: number; readonly scale: number; readonly strength: number; readonly glow?: number };
}

/** The attachment sockets every tower model has (tools/art/skins/sockets.mjs documents where each one is measured). */
export const SOCKET_RINGS = ["crest", "wall", "band", "corner"] as const;
export type SocketRing = (typeof SOCKET_RINGS)[number];
export type SocketName = "apex" | "top" | `${SocketRing}${0 | 1 | 2 | 3}`;
export const SOCKET_NAMES: readonly SocketName[] = ["apex", "top", ...SOCKET_RINGS.flatMap((r) => [0, 1, 2, 3].map((i) => `${r}${i}` as SocketName))];

/**
 * An attachment's ambient motion, evaluated on the GPU (kit/kitMaterial.ts) in the piece's own frame: +Y up, +Z out
 * of the wall, +X to its right. Rates in Hz unless named otherwise; every instance gets its own phase.
 */
export interface Motion {
  /** Turns about the piece's up axis, radians a second (the gilded gem). */
  readonly spin?: number;
  /**
   * Turns about the piece's OUT axis, radians a second: a wheel on a wall face turning in its own plane (bronze's cogs).
   * Applied first, in the piece's own frame, then sway and spin.
   */
  readonly roll?: number;
  /** Rises and falls along up: amplitude in metres. */
  readonly bob?: number;
  /** Rocks about its right axis (a hanging pennant, a swinging lamp): amplitude in degrees. */
  readonly sway?: number;
  /** Breathes in scale: amplitude as a fraction (0.08 = ±8 %). */
  readonly pulse?: number;
  /**
   * Cloth wave: vertices displaced along out by `wave` metres × the vertex's weight (COLOR_0.r authored in Blender: 0
   * at the pole, 1 at the free edge), a travelling wave along the piece's right axis.
   */
  readonly wave?: number;
  /** Spatial frequency of the wave, radians per metre along right (default 18). */
  readonly waveK?: number;
  /** A twinkle: the piece grows from nothing and fades back, `twinkle` times a second (a glint, a star). */
  readonly twinkle?: number;
  /** Emissive breathing: ±fraction of the piece's authored emissive (a gem's glow, a rune). */
  readonly glow?: number;
  /** Rate of bob / sway / pulse / wave / glow (Hz, default 0.5). */
  readonly hz?: number;
  /**
   * Instead of a random phase per instance, a phase that runs along the ground: radians per metre of (x + z) of the
   * piece's position (road props: the glow rolls down the road as a slow wave). 0 / unset: random phases.
   */
  readonly travel?: number;
  /** Big kits (kit/bigTypes.ts): circle the mount point (copies share the orbit). */
  readonly orbit?: Orbit;
  /** Big kits: rock about a hinge (wings, banners, shutters), or strike in time with the shots (`drive: "charge"`). */
  readonly flap?: Flap;
  /** Big kits: bands of glow and swell travelling along the piece (lava, water, runes). */
  readonly flow?: Flow;
  /** Big kits: glow and swell building to the tower's shot (kit/charge.ts). */
  readonly charge?: Charge;
}

/** Which towers a use applies to. Omitted: every tower type and tier. */
export interface UseFilter {
  /** Tower archetype ids ("archer", "frost", …). */
  readonly towers?: readonly string[];
  /** Tiers 1..3. */
  readonly tiers?: readonly number[];
}

/**
 * A use's placement on one tower type, overriding the use's own `at` / `scale` / `offset` / `yaw` there (Towers v2: the
 * same piece and the same motion row — the GPU table holds one row per use — fitted to a different silhouette, such as
 * a crown sat low on the arcane spire's collar or a pennant moved to the mortar's shell locker).
 */
export interface AttachmentFit {
  readonly at?: readonly (SocketName | SocketRing)[];
  readonly scale?: number | readonly [number, number, number];
  readonly offset?: readonly [number, number, number];
  readonly yaw?: number;
  /** A fitted placement there instead (or `null`: this tower mounts the use on its sockets, unfitted). */
  readonly fitTo?: FitMode | FitSpec | null;
}

/** One prop of the skin's kit GLB placed on one or more sockets. */
export interface AttachmentUse extends UseFilter {
  /** The piece's node name in the kit GLB. */
  readonly piece: string;
  /** The sockets it mounts on: names, or a ring ("wall" = wall0..3, "crest", "band", "corner"). */
  readonly at: readonly (SocketName | SocketRing)[];
  /** Uniform scale on the authored size (default 1); a [t1, t2, t3] triple grows it with the tier. */
  readonly scale?: number | readonly [number, number, number];
  /** Offset in the socket's frame, metres: [right, up, out]. */
  readonly offset?: readonly [number, number, number];
  /** Turn about the socket's up axis, degrees. */
  readonly yaw?: number;
  /** Ambient motion; overrides the piece's own `td_motion` extras from Blender. */
  readonly motion?: Motion;
  /** Detail-only (twinkles, small glints): hidden at the low and lowest tiers and when effects are reduced. */
  readonly detail?: boolean;
  /** Per tower type: a different placement of this use there (kitGeometry.ts `usesFor` applies it). */
  readonly fit?: Readonly<Record<string, AttachmentFit>>;
  /**
   * Big kits: fitted to an envelope measured on each model (kit/fit.ts; anchors.json `dims`) instead of mounted on
   * sockets — one instance per tower, `at` is then ignored. The piece is authored in the unit envelope.
   */
  readonly fitTo?: FitMode | FitSpec;
  /** Mount only where the kit really hides this stock group (a replacement roof skips lightning, which keeps its own). */
  readonly ifHidden?: HideGroup;
  /** Mount only where this stock group still stands (the fallback crown on a tower whose roof could not be hidden). */
  readonly ifShown?: HideGroup;
}

/**
 * An idle VFX: a burst of sprite particles from a socket every few seconds (kit/fx.ts; level only). Kinds: `sparkles`
 * (rising stars), `embers` (rising, cooling glows), `motes` (slow drifting stars: fireflies), `glints` (stars in
 * place), `steam` (a soft white puff that rises and spreads), `drips` (droplets that fall off the socket), `leaves` (leaf sprites drifting down and turning), `inward` (sparks
 * drawn in from a shell onto the socket).
 */
export interface IdleFx {
  readonly kind: IdleKind;
  readonly colour: number;
  /** Seconds between bursts, min and max. */
  readonly every: readonly [number, number];
  /** Where they come from. */
  readonly at: SocketName;
  /** Particles a burst (budget: KIT_BUDGET.IdleParticles). */
  readonly count: number;
  /** Spread radius in metres (default 0.2). */
  readonly radius?: number;
  /** Metres above the socket the burst starts (a prop's mouth: bronze's steam pipe; default 0). */
  readonly lift?: number;
  /** A second burst from the same socket at the same moment (verdigris: drips and fireflies). Counts toward the budget. */
  readonly also?: { readonly kind: IdleKind; readonly colour: number; readonly count: number; readonly radius?: number };
  /** Per tower type: another socket / lift there (the prop the burst comes from is mounted elsewhere on that tower). */
  readonly fit?: Readonly<Record<string, { readonly at?: SocketName; readonly lift?: number }>>;
}
export type IdleKind = "sparkles" | "embers" | "motes" | "glints" | "steam" | "drips" | "leaves" | "inward";

/** An attack flourish on top of the tower's own shot VFX (kit/fx.ts; level only). */
export interface Flourish {
  /**
   * At the target when the round lands: `glint` (stars and a flash), `sparks` (a spray of hot sparks and a flash),
   * `ring` (a flat shock ring), `splash` (a ring with droplets thrown up and falling back), `blaze` (embers bursting up
   * with a wide soft heat flash that swells and fades: the heat shimmer), `radiant` (a white flash, an upright halo ring
   * and sparkles), `warfire` (a flash, sparks and rising embers), `leafburst` (leaves and bark chips thrown out),
   * `implode` (a ring that collapses onto the point with sparks drawn in).
   */
  readonly impact?: FlourishImpact;
  /** At the muzzle as the shot leaves (`kind` default `glint`). */
  readonly muzzle?: FlourishMuzzle;
  /**
   * Big kits: impact variants, played in turn shot by shot (round-robin per tower) instead of `impact`, so a premium
   * skin's hits do not all look alike.
   */
  readonly impacts?: readonly FlourishImpact[];
  /** Big kits: sparks drawn in onto the muzzle for `lead` s (default 0.35) before the shot leaves: the wind-up. */
  readonly charge?: { readonly colour: number; readonly count?: number; readonly lead?: number };
  /** Big kits: per tower type, another impact / muzzle / variants there (a cannon's blast, a frost tower's shatter). */
  readonly towers?: Readonly<Record<string, Pick<Flourish, "impact" | "impacts" | "muzzle" | "charge">>>;
  /**
   * Flourish every shot (Starfall): the flourish gap (kit/fx.ts SkinFx.FlourishGapSeconds) no longer skips a fast
   * tower's shots; a shot inside the gap gets a half-size burst instead, so the particle load stays bounded.
   */
  readonly everyShot?: boolean;
  /**
   * Big kits: a hit burst on the tower itself on each round's release — where a `drive: "charge"` flap lands (the
   * forge hammer on its anvil): the `sparks` impact at socket `at` + `offset` (the socket's frame, model metres, as a
   * use's offset); `fit` per tower type replaces the offset (a smaller forge on the slim shafts).
   */
  readonly strike?: { readonly colour: number; readonly count?: number; readonly at: SocketName; readonly offset?: readonly [number, number, number]; readonly fit?: Readonly<Record<string, readonly [number, number, number]>> };
}

/**
 * The flourish impact kinds; big kits add `shatter` (shards thrown out and falling hard, a cold flash) and `pillar` (a
 * column of sparkles and embers rising from the hit, a flash at its foot).
 */
export type ImpactKind = "glint" | "sparks" | "ring" | "splash" | "blaze" | "radiant" | "warfire" | "leafburst" | "implode" | "shatter" | "pillar";
export interface FlourishImpact { readonly kind: ImpactKind; readonly colour: number; readonly count?: number }
/** A muzzle flourish: `glint` (default: stars), `sparks` (a hot spray), `ring` (an upright shock ring), `embers`. */
export interface FlourishMuzzle { readonly colour: number; readonly count?: number; readonly kind?: "glint" | "sparks" | "ring" | "embers" }

/** A tower skin's kit. */
export interface TowerKit {
  readonly id: string;
  /**
   * The kit GLB's asset name (the asset library loads `hq/<asset>.glb`, KTX2 twin `hq/ktx2/<asset>.glb`), e.g.
   * "skins/gilded/kit"; null for a materials-only kit.
   */
  readonly asset: string | null;
  readonly slots: Partial<Record<SlotName, SlotLook>>;
  readonly attachments: readonly AttachmentUse[];
  readonly idle?: IdleFx;
  readonly flourish?: Flourish;
  /** The tower the thumbnail and the Skins screen show it on (default archer t3). */
  readonly preview?: { readonly tower: string; readonly tier: number };
  /**
   * Big kits: stock parts this kit hides where it mounts (kit/hide.ts: never the element, the glow, the drum or the
   * weapon; a part something stands on stays, see HIDE_REFUSED). Replace them with fitted pieces (`fitTo`, `ifHidden`).
   */
  readonly hide?: readonly HideGroup[];
  /** Big kits: per tower type, another hide list there (`[]`: hide nothing on that tower). */
  readonly hideFit?: Readonly<Record<string, readonly HideGroup[]>>;
  /**
   * Big kits: the kit's own tower models `tower_<id>_t<n>__<model>.glb` (kit/towerModels.ts), used wherever the
   * manifest ships one; elsewhere the stock model wears the kit as usual.
   */
  readonly model?: string;
  /** Which kit layers still go on a shipped `model` (default none: the model is the look). */
  readonly onModel?: ModelLayers;
  /**
   * Mount the attachments (and hide parts) on the tier-IV branch models `tower_<id>_t4_<branch>` too (as tier 3 for
   * `tiers` and `scale`). Off by default: the kits before 2026-10-21 dressed tier IV in their materials only.
   */
  readonly tier4?: boolean;
  /** Big kits: how the kit's own `model` surfaces are lit in the game (kit/modelSurface.ts). */
  readonly surface?: ModelSurface;
}

// the whole-model kits (fortress, troop, pet) and the road kit live in kit/modelKitTypes.ts; re-exported here
export type { FortressKit, CompanionSpec, AmbienceSpec, TroopKit, TroopFx, PartPoint, PetKit, RoadPiece, RoadKit, RoadSurface, RoadPaving } from "./modelKitTypes";

/**
 * Budgets (per tower: what a phone pays for 16 skinned towers; checked by the kit tests where they can be).
 * Attachments are merged into ONE skinned draw per tower, sharing the tower's own bones (no extra skeleton upload).
 */
export const KIT_BUDGET = {
  /** Triangles of all attachments on one tower model (keeps it under TowerLod.MinTriangles: no LOD copies). */
  TrisPerTower: 1900,
  /**
   * A big kit's triangles on one tower model (it hides or fits: kit/hide.ts, kit/fit.ts; orbit copies counted). A
   * hidden roof gives back 900–26 000 triangles (ballista … archer ranger), so the tower still costs less than stock +
   * a classic kit. The attachment mesh then gets the far-zoom LOD copy like any tower mesh (views/towerLod.ts).
   */
  TrisPerTowerBig: 3000,
  /** Triangles of one authored piece. */
  TrisPerPiece: 600,
  /** Attachment instances on one tower. */
  AttachmentsPerTower: 20,
  /** Uses in one kit (the GPU table's size, kit/kitMaterial.ts MaxUses). */
  UsesPerKit: 12,
  /** Sprite particles per idle burst, and per flourish. */
  IdleParticles: 6,
  FlourishParticles: 8,
  /** The kit GLB: its texture edge (one atlas, px), and its packed size in bytes. */
  TexturePx: 512,
  KitBytes: 400_000,
  /** A road kit: instances along one road. */
  RoadInstances: 160,
} as const;
